import { ImagePlus, Replace, Trash2, UploadCloud } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'

import { cn } from '../../lib/cn'
import {
  ACCEPTED_IMAGE_EXTENSIONS,
  ACCEPTED_IMAGE_MIME_TYPES,
  MAX_IMAGE_SIZE_BYTES,
  getImageValidationError,
} from '../../lib/image-validation'
import { Button } from '../ui/Button'

interface ImageDropzoneProps {
  value: File | null
  onChange: (file: File | null) => void
  disabled?: boolean
  error?: string
  currentImageUrl?: string | null
  currentImageName?: string | null
}

function rejectionMessage(rejection: FileRejection) {
  const firstError = rejection.errors[0]
  if (firstError?.code === 'file-too-large') return 'La imagen no puede superar 5 MB.'
  return 'La imagen debe ser JPG, JPEG, PNG o WEBP.'
}

export function ImageDropzone({
  value,
  onChange,
  disabled,
  error,
  currentImageUrl,
  currentImageName,
}: ImageDropzoneProps) {
  const id = useId()
  const [localError, setLocalError] = useState<string>()
  const [previewUrl, setPreviewUrl] = useState<string>()

  useEffect(() => {
    if (!value || typeof URL.createObjectURL !== 'function') {
      setPreviewUrl(undefined)
      return
    }

    const objectUrl = URL.createObjectURL(value)
    setPreviewUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [value])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    maxFiles: 1,
    maxSize: MAX_IMAGE_SIZE_BYTES,
    multiple: false,
    noClick: Boolean(value ?? currentImageUrl),
    noKeyboard: Boolean(value ?? currentImageUrl),
    disabled,
    onDropAccepted: ([file]) => {
      if (!file) return
      const validationError = getImageValidationError(file)
      if (validationError) {
        setLocalError(validationError)
        return
      }
      setLocalError(undefined)
      onChange(file)
    },
    onDropRejected: ([rejection]) =>
      setLocalError(rejection ? rejectionMessage(rejection) : undefined),
  })

  const shownError = error ?? localError
  const shownPreview = previewUrl ?? (!value ? (currentImageUrl ?? undefined) : undefined)
  const shownName = value?.name ?? currentImageName

  return (
    <div>
      <div
        {...getRootProps({
          className: cn(
            'group relative overflow-hidden rounded-2xl border-2 border-dashed bg-white transition',
            isDragActive ? 'border-clay bg-orange-50' : 'border-ink/15 hover:border-moss/50',
            shownError && 'border-red-500',
            disabled && 'cursor-not-allowed opacity-60',
            !value && 'cursor-pointer',
          ),
        })}
      >
        <input
          {...getInputProps({ id, 'aria-describedby': shownError ? `${id}-error` : `${id}-help` })}
        />
        {shownPreview ? (
          <div className="grid gap-4 p-4 sm:grid-cols-[7rem_1fr] sm:items-center">
            <div className="grid h-28 place-items-center overflow-hidden rounded-xl border bg-white">
              <img
                src={shownPreview}
                alt="Vista previa de la imagen"
                className="h-full w-full object-contain"
              />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{shownName ?? 'Imagen actual'}</p>
              <p className="mt-1 text-xs text-ink/50">
                {value
                  ? `${(value.size / 1024).toFixed(0)} KB · Lista para subir`
                  : 'Imagen guardada actualmente'}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={open}
                  disabled={disabled}
                >
                  <Replace aria-hidden="true" className="h-4 w-4" />
                  {value ? 'Cambiar' : 'Subir nueva imagen'}
                </Button>
                {value && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={(event) => {
                      event.stopPropagation()
                      setLocalError(undefined)
                      onChange(null)
                    }}
                    disabled={disabled}
                  >
                    <Trash2 aria-hidden="true" className="h-4 w-4" />
                    Quitar selección
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex min-h-40 flex-col items-center justify-center px-6 py-8 text-center">
            <span className="mb-3 grid h-11 w-11 place-items-center rounded-xl bg-mist text-moss">
              {isDragActive ? (
                <ImagePlus aria-hidden="true" className="h-5 w-5" />
              ) : (
                <UploadCloud aria-hidden="true" className="h-5 w-5" />
              )}
            </span>
            <p className="text-sm font-semibold">
              {isDragActive ? 'Suelta la imagen aquí' : 'Arrastra una imagen o selecciónala'}
            </p>
            <p id={`${id}-help`} className="mt-1 text-xs text-ink/50">
              {ACCEPTED_IMAGE_EXTENSIONS.join(', ').toUpperCase()} · máximo 5 MB
            </p>
            <span className="sr-only">Tipos MIME: {ACCEPTED_IMAGE_MIME_TYPES.join(', ')}</span>
          </div>
        )}
      </div>
      {shownError && (
        <p id={`${id}-error`} className="form-error" role="alert">
          {shownError}
        </p>
      )}
    </div>
  )
}
